import { useState } from 'react';
import Box from '@mui/material/Box';
import { Autocomplete, TextField, Popper, styled } from '@mui/material';
import Countries from '../../modules/countries';

const StyledPopper = styled(Popper)({
  width: 'auto',
  minWidth: 250,
});

interface CountrySelectorProps {
  handleFieldChange: (value: string) => void;
  country: string;
  label: string;
  showFlags?: boolean;
  showCountryCodes?: boolean;
  showCountryLabel?: boolean;
  showPhoneCodes?: boolean;
  selectedDisplayField: 'code' | 'label' | 'phone';
  valueField: 'code' | 'label' | 'phone';
  errors: string[];
  autoCompleteStyles?: Record<string, any>;
  textFieldStyles?: Record<string, any>;
}

export default function CountrySelector({
  handleFieldChange,
  country,
  label,
  showFlags = false,
  showCountryLabel = false,
  showCountryCodes = false,
  showPhoneCodes = false,
  selectedDisplayField,
  valueField,
  errors,
  autoCompleteStyles,
}: CountrySelectorProps) {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const countryObject = Countries.find((c) => c[valueField] === country);
    return countryObject || null;
  });

  const handleSelectionChange = (_: any, newValue: any) => {
    if (newValue) {
      setSelectedCountry(newValue);
      handleFieldChange(newValue[valueField]);
    } else {
      setSelectedCountry(null);
      handleFieldChange('');
    }
  };

  return (
    <Autocomplete
      sx={autoCompleteStyles}
      id="country-selector"
      options={Countries}
      autoHighlight
      getOptionLabel={(option) => selectedDisplayField === 'phone' ? `+${option.phone}` : option[selectedDisplayField]}
      value={selectedCountry}
      onChange={handleSelectionChange}
      isOptionEqualToValue={(option, value) => option[valueField] === value[valueField]}
      PopperComponent={(props) => <StyledPopper {...props} />}
      filterOptions={(options, state) =>
        options.filter(
          (option) =>
            option.label.toLowerCase().includes(state.inputValue.toLowerCase()) ||
            option.phone.includes(state.inputValue.replace('+', ''))
        )
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
          {...props}
          key={option.code}
        >
          {showFlags && (
            <img
              loading="lazy"
              width="20"
              srcSet={`//flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`//flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
          )}
          {showCountryLabel && `${option.label}`} {showCountryCodes && `(${option.code})`} {showPhoneCodes && `+${option.phone}`}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          value={
            selectedCountry
              ? selectedCountry[selectedDisplayField]
              : ''
          }
          onChange={(e) => handleFieldChange(e.target.value)}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',
          }}
          error={(errors && errors?.length > 0)}
          helperText={
            errors?.join('\n')
          }
        />
      )}
    />
  );
}
